import * as React from "react";
import createDebounce from "redux-debounced";
import { applyMiddleware, combineReducers, compose, createStore } from "redux";
import { Provider } from "react-redux";
import thunk from "redux-thunk";
import ElstrCore from "./ElstrCore";
import ElstrUser from "./ElstrUser";
import ElstrLanguage from "./ElstrLanguage";
import ElstrConfig from "./ElstrConfig";
import reduxImmutableStateInvariant from "redux-immutable-state-invariant";
import ElstrLocalStorage from "./ElstrLocalStorage";
import { ELSTR_BACKEND_VERSION } from "./constants/BackendVersion";
import { HistoryRouter as Router } from "redux-first-history/rr6";
import { createReduxHistoryContext } from "redux-first-history";
import { createRoot, Root } from "react-dom/client";

declare const window: any;

// Next line has to be ignored in typescript due to library problems that can be only solved inside the lib
const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: ElstrCore.history,
});

export default class ElstrBaseApp extends ElstrCore {
  static ELSTR_BACKEND_VERSION = ELSTR_BACKEND_VERSION.BE2;

  rootComp: React.ReactElement<any> = (<div />);

  root: Root | null = null;

  /**
   * Render Root Layout / unmount if is already there
   * @param rootComponent
   */
  renderRoot = () => {
    if (!this.root) {
      const container = document.getElementById("root");
      this.root = createRoot(container!); // createRoot(container!) if you use TypeScript
    }
    const history = createReduxHistory(ElstrCore.store);
    const CR = <Router history={history}>{this.rootComp}</Router>;
    this.root.render(<Provider store={ElstrCore.store}>{CR}</Provider>);
  };

  static reuseState(newState, oldState) {
    if (newState === oldState) {
      return oldState;
    }
    if (
      oldState === undefined ||
      oldState === null ||
      newState === undefined ||
      newState === null
    ) {
      return newState;
    }
    if (Array.isArray(newState)) {
      if (!Array.isArray(oldState)) {
        return newState;
      }
      if (newState.length !== oldState.length) {
        return newState;
      }
      let canReuse = true;
      for (let i = 0; i < newState.length; i++) {
        let newSubState = newState[i];
        const oldSubState = oldState[i];
        if (newSubState !== oldSubState) {
          newSubState = ElstrBaseApp.reuseState(newSubState, oldSubState);
          if (newSubState !== oldSubState) {
            canReuse = false;
          }

          // additional check for libraries like immerjs which use Object.freeze mechanism for state changes
          // in that case the following assignment isn't possible and will be skipped
          if (!Object.isFrozen(newState)) {
            newState[i] = newSubState;
          }
        }
      }
      if (canReuse) {
        return oldState;
      } else {
        return newState;
      }
    } else if (typeof newState === "object") {
      if (typeof oldState !== "object") {
        return newState;
      }
      let canReuse = true;
      // reuse substate if possible and check if all new substates can be reused
      for (const key in newState) {
        if (newState.hasOwnProperty(key)) {
          if (oldState.hasOwnProperty(key)) {
            let newSubState = newState[key];
            const oldSubState = oldState[key];
            if (newSubState !== oldSubState) {
              newSubState = ElstrBaseApp.reuseState(newSubState, oldSubState);
              if (newSubState !== oldSubState) {
                canReuse = false;
              }

              // additional check for libraries like immerjs which use Object.freeze mechanism for state changes
              // in that case the following assignment isn't possible and will be skipped
              if (!Object.isFrozen(newState)) {
                newState[key] = newSubState;
              }
            }
          } else {
            canReuse = false;
          }
        }
      }
      if (canReuse) {
        // check that all old substates were reused
        for (const key in oldState) {
          if (oldState.hasOwnProperty(key)) {
            if (!newState.hasOwnProperty(key)) {
              canReuse = false;
              break;
            }
          }
        }
      }
      if (canReuse) {
        return oldState;
      } else {
        return newState;
      }
    } else {
      return newState;
    }
  }

  combineReducersAndReuseState(reducers: any) {
    const combine = combineReducers(reducers);
    // @ts-ignore
    return function combineAndReuse(oldState, action) {
      // @ts-ignore
      let newState = combine(oldState, action);
      newState = ElstrBaseApp.reuseState(newState, oldState);
      return newState;
    };
  }

  /**
   * Setup Redux include React Router Reducer (Only Debug Mode)
   * @param reducers
   * @param initStore
   * @param appMiddleware
   */
  setupRedux(reducers: any, initStore: any, appMiddleware: any[]) {
    if (ElstrCore.store) {
      ElstrCore.store.replaceReducer(
        this.combineReducersAndReuseState({
          ...reducers,
          router: routerReducer,
        }),
      );
      return;
    }

    // Debounce enable debounce possiblity
    const middleware = [createDebounce(), thunk, routerMiddleware, ...appMiddleware];

    // Redux middleware that spits an error on you when you try to mutate your state either inside a dispatch or
    // between dispatches. For development use only!
    // As said above, don't use this in production! It involves a lot of object copying and will degrade ' +
    // 'your app's performance. This is intended to be a tool to aid you in development and help you catch bugs.
    if (shouldAddReduxImmutableStateInvariant()) middleware.push(reduxImmutableStateInvariant());

    const composeEnhancers =
      typeof window === "object" && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
        ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({})
        : compose;

    const enhancer = composeEnhancers(applyMiddleware(...middleware));

    // Create Redux Store
    ElstrCore.store = createStore(
      this.combineReducersAndReuseState({
        ...reducers,
        router: routerReducer,
      }),
      initStore,
      enhancer,
    );
  }

  language: ElstrLanguage = new ElstrLanguage(this.renderRoot);

  config: ElstrConfig = new ElstrConfig();

  user: ElstrUser = new ElstrUser(this.renderRoot);

  static ELS: ElstrLocalStorage = new ElstrLocalStorage();

  /**
   * Helper function which setup the common Elstr stuff
   * @param reducers all reducers to use by the app
   * @param initState The initial Sate at the very first start of the app
   * @param rootComponent Module which is provided by Webpack (needed for HMR)
   * @param appMiddleware
   */
  setup(
    reducers: any[],
    initState,
    rootComponent: React.ReactElement<any>,
    appMiddleware: any[] = [],
  ) {
    this.setupRedux(Object.assign({}, ...reducers), initState, appMiddleware);
    if (rootComponent) {
      this.rootComp = rootComponent;
      this.renderRoot();
    }
  }

  /**
   * Helper array for detecting state mutations
   */
  static knownObjects = new Map();

  /**
   * Helper counter for detecting state mutations
   */
  static knownObjectCount = 0;

  /**
   * Helper function for detecting undesired object clones, returns a unique number for each object
   * NOTE: Don't use in production because adding objects to a map prevent the objects from being garbage collected
   * @param obj
   */
  static getObjectId(obj) {
    if (obj === null || obj === undefined) {
      return -1;
    }
    const i = ElstrBaseApp.knownObjects.get(obj);
    if (i) {
      return i;
    } else {
      ElstrBaseApp.knownObjectCount = ElstrBaseApp.knownObjectCount + 1;
      ElstrBaseApp.knownObjects.set(obj, ElstrBaseApp.knownObjectCount);
      return ElstrBaseApp.knownObjectCount;
    }
  }
}

function shouldAddReduxImmutableStateInvariant() {
  if (process.env.NODE_ENV === "production") {
    return false;
  }

  if (
    process.env.REACT_APP_REDUX_IMMUTABLE_STATE_INVARIANT &&
    process.env.REACT_APP_REDUX_IMMUTABLE_STATE_INVARIANT === "false"
  ) {
    return false;
  }

  return true;
}
